import React from 'react';
import { useQuery } from '@tanstack/react-query';

import type { Participation } from '../../../types';
import type { GetParticipationsArgs } from '../../../Domain/Employees/Services/EmployeeService';
import { employeeService } from '../../../Domain/Employees/Services/EmployeeService';

import { useSnakeBar } from '../../useSnakeBar';
import type { UseQueryWrapper } from '../../../types/useQuery.types';

type GetParticipationsQueryVariables = {
  [Key in keyof GetParticipationsArgs]: GetParticipationsArgs[Key] | undefined;
};

const queryFn =
  ({ connectionId, employeeId }: GetParticipationsQueryVariables) =>
  async () => {
    if (connectionId && employeeId) {
      const response = await employeeService.getParticipations({ connectionId, employeeId });
      return response.data;
    }
  };

const QUERY_KEY = 'getParticipations';

export const useEmployeeGetParticipationsQuery: UseQueryWrapper<Participation[], GetParticipationsQueryVariables> = ({
  variables,
  options = {},
}) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  const select = React.useCallback((data: any) => {
    return data['hydra:member'] || [];
  }, []);

  return useQuery<Participation[]>([QUERY_KEY, variables.connectionId, variables.employeeId], queryFn(variables), {
    select,
    onError,
    ...options,
  });
};
